import { useState, useEffect } from "react";
import { getFunctions, httpsCallable } from "firebase/functions";
import { useToast } from "../../contexts/ToastContext";
import { peso, fmtDate } from "../../utils/billingFormat";
import "../../styles/components/settings-pages.css";

const functions = getFunctions(undefined, "asia-southeast1");
const listInvoicesFn = httpsCallable(functions, "listInvoices");

// Simplified, non-tech-friendly statuses (mapped server-side in invoiceHelpers).
const STATUS_BADGE = {
  paid: { label: "Paid", bg: "var(--ui-success-tint)", fg: "var(--ui-success-text)" },
  failed: { label: "Failed", bg: "var(--ui-danger-tint)", fg: "var(--ui-danger-text)" },
  pending: { label: "Pending", bg: "var(--ui-warning-tint)", fg: "var(--ui-warning-text)" },
  unpaid: { label: "Unpaid", bg: "var(--ui-surface-recessed)", fg: "var(--ui-text-secondary)" },
};

const InvoiceHistoryPage = () => {
  const toast = useToast();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(false);
      try {
        const { data } = await listInvoicesFn();
        if (!cancelled) setInvoices(data?.invoices || []);
      } catch (e) {
        if (!cancelled) {
          setError(true);
          toast.error(e?.message || "Couldn't load invoices. Please try again.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [toast]);

  const th = { padding: "10px 8px", fontWeight: 600 };
  const td = { padding: "10px 8px", whiteSpace: "nowrap" };

  return (
    <div>
      <h1 className="settings-page-title">Invoice History</h1>
      <p className="settings-page-subtitle">View and download your invoices and receipts</p>

      <div className="settings-card" style={{ padding: 24 }}>
        {loading ? (
          <p style={{ fontSize: "0.875rem", color: "var(--ui-text-muted)" }}>Loading invoices…</p>
        ) : error ? (
          <p style={{ fontSize: "0.875rem", color: "var(--ui-danger-text)" }}>
            Couldn't load invoices. Please refresh to try again.
          </p>
        ) : invoices.length === 0 ? (
          <p style={{ fontSize: "0.875rem", color: "var(--ui-text-faint)" }}>
            No invoices yet — they'll appear here after your first payment.
          </p>
        ) : (
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.875rem" }}>
            <thead>
              <tr style={{ textAlign: "left", color: "var(--ui-text-muted)", borderBottom: "1px solid var(--ui-border)" }}>
                <th style={{ ...th, padding: "10px 8px 10px 0" }}>Billing period</th>
                <th style={th}>Amount</th>
                <th style={th}>Status</th>
                <th style={th}>Invoice #</th>
                <th style={{ ...th, padding: "10px 0 10px 8px" }} aria-label="Actions"></th>
              </tr>
            </thead>
            <tbody>
              {invoices.map((inv) => {
                const badge = STATUS_BADGE[inv.simpleStatus] || STATUS_BADGE.pending;
                return (
                  <tr key={inv.id} style={{ borderBottom: "1px solid var(--ui-surface-recessed)", color: "var(--ui-text-secondary)" }}>
                    <td style={{ ...td, padding: "10px 8px 10px 0" }}>
                      {fmtDate(inv.periodStart)} – {fmtDate(inv.periodEnd)}
                    </td>
                    <td style={td}>{peso(inv.amount)}</td>
                    <td style={{ padding: "10px 8px" }}>
                      <span
                        style={{
                          background: badge.bg,
                          color: badge.fg,
                          borderRadius: 999,
                          padding: "3px 10px",
                          fontWeight: 600,
                          fontSize: "0.75rem",
                        }}
                      >
                        {badge.label}
                      </span>
                    </td>
                    <td style={{ ...td, color: "var(--ui-text-muted)" }}>{inv.number || "—"}</td>
                    <td style={{ ...td, padding: "10px 0 10px 8px" }}>
                      {inv.hostedUrl ? (
                        <a
                          href={inv.hostedUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ color: "var(--ui-primary)", fontWeight: 600, textDecoration: "none" }}
                        >
                          View / Download
                        </a>
                      ) : (
                        "—"
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default InvoiceHistoryPage;
